import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import {
  Users, Shield, Plug, ChevronRight, Settings, Activity, UserCheck, CreditCard, CheckSquare, FileBadge, Archive, Banknote
} from 'lucide-react';

const ControlPanel = () => {
  const { user, isSuperAdmin } = useAuth();
  
  // Check if user can view activity logs (Directors or Super Admins)
  const canViewActivityLogs = isSuperAdmin() || user?.can_approve_transfers;

  const controlItems = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: Users,
      path: '/control-panel/users',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Payment Accounts',
      description: 'Manage payment gateway accounts and credentials',
      icon: CreditCard,
      path: '/payment-accounts',
      color: 'bg-emerald-100 text-emerald-600'
    },
    // Cashfree KYC - hidden from kiosk volunteers
    ...(user?.role !== 'kiosk_volunteer' ? [{
      title: 'Cashfree KYC',
      description: 'Manage Cashfree vendor KYC for Easy Split (commission + settlement)',
      icon: FileBadge,
      path: '/cashfree-kyc',
      color: 'bg-pink-100 text-pink-600'
    }] : []),
    // Razorpay Route Accounts - hidden from kiosk volunteers
    ...(user?.role !== 'kiosk_volunteer' ? [{
      title: 'Razorpay Route Accounts',
      description: 'Manage Razorpay Linked Accounts for per-event payment splits',
      icon: Banknote,
      path: '/razorpay-route',
      color: 'bg-indigo-100 text-indigo-600'
    }] : []),
    // Approvals - only for users with approval permission
    ...(user?.can_approve_transfers ? [{
      title: 'Approvals',
      description: 'Review and approve pending fund transfers',
      icon: CheckSquare,
      path: '/approvals',
      color: 'bg-orange-100 text-orange-600'
    }] : []),
    {
      title: 'Kiosk Volunteers',
      description: 'Manage volunteers for on-site kiosk registration',
      icon: UserCheck,
      path: '/kiosk-volunteers',
      color: 'bg-teal-100 text-teal-600'
    },
    // Platform-wide integrations + DKIM are Super-Admin only
    ...(isSuperAdmin() ? [
      {
        title: 'Integrations',
        description: 'Configure Email and Google Sheets integrations',
        icon: Plug,
        path: '/control-panel/integrations',
        color: 'bg-violet-100 text-violet-600'
      },
      {
        title: 'DKIM Registration',
        description: 'Manage email domains and sender verification',
        icon: Shield,
        path: '/control-panel/dkim',
        color: 'bg-green-100 text-green-600'
      },
    ] : []),
    // Instamojo For Accounts — Director / Super Admin only (password-gated on entry)
    ...(canViewActivityLogs ? [{
      title: 'Instamojo For Accounts',
      description: 'Finance view of Instamojo transactions, payouts, and reports',
      icon: Archive,
      path: '/instamojo',
      color: 'bg-indigo-100 text-indigo-600'
    }] : []),
    // Activity Logs - only for Directors and Super Admins
    ...(canViewActivityLogs ? [{
      title: 'Activity Logs',
      description: 'View all system changes and audit trail',
      icon: Activity,
      path: '/activity-logs',
      color: 'bg-amber-100 text-amber-600'
    }] : [])
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900">Control Panel</h1>
              <p className="text-sm text-slate-500 mt-0.5">System settings and configurations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {controlItems.map((item) => (
            <Link key={item.path} to={item.path}>
              <Card className="bg-white border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-200 cursor-pointer group h-full">
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-4">{item.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{item.description}</p>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ControlPanel;
